import RadioGroup from '@mui/material/RadioGroup';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

import { hardRedirectTo } from '../../../utils';
import Button from '../../generic/Button';
import LabeledRadio from '../../generic/Forms/LabeledRadio';
import UseDifferentAccountButton from '../components/UseDifferentAccountButton';
import type { Candidate, SignupSuggestion, SsoMethod } from '../../../../shared/types/routes/auth/v2';

import sharedStyles from '../Login.module.less';
import styles from './OrgPickerStep.module.less';

export type OrgPickerStepProps = {
  candidates: Candidate[];
  signupSuggestions: SignupSuggestion[];
  onPickCandidate: (org: Candidate) => void;
  onBack: () => void;
};

const SSO_METHOD_LABEL: Record<SsoMethod, string> = {
  google: 'Google',
  saml: 'SAML',
  ldap: 'LDAP',
};

const ssoEntryUrl = (suggestion: SignupSuggestion): string | null => {
  switch (suggestion.ssoMethod) {
    case 'saml':
      return `/account/saml/${suggestion.organizationId}`;
    case 'google':
      return '/account/google';
    case 'ldap':
      // LDAP is a form-only login with no redirect entry point, so its suggestion is disabled before it reaches here.
      return null;
    default: {
      const exhaustiveCheck: never = suggestion.ssoMethod;
      return exhaustiveCheck;
    }
  }
};

/**
 * The organization-picker step. When a resolved identifier maps to more than one member org — or, when the user has no
 * member org, to domain-claim orgs they can join via SSO — this renders a radio group so the user chooses which org to
 * authenticate into. Member-org candidates take priority; signup suggestions show only when there are no candidates.
 */
const OrgPickerStep = ({ candidates, signupSuggestions, onPickCandidate, onBack }: OrgPickerStepProps) => {
  const hasCandidates = candidates.length > 0;
  // Default to the first selectable option so the user can continue without an extra click. Member candidates take
  // priority; otherwise the first non-LDAP suggestion (LDAP suggestions are disabled and not pickable).
  const defaultOrgId = hasCandidates
    ? candidates[0]?.organizationId
    : signupSuggestions.find(suggestion => suggestion.ssoMethod !== 'ldap')?.organizationId;
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(defaultOrgId ?? null);
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);

  // Move focus to the first selectable option on mount so keyboard users land in the group without a stray Tab.
  useEffect(() => {
    fieldsetRef.current?.querySelector<HTMLInputElement>('input[type="radio"]:not(:disabled)')?.focus();
  }, []);

  const handleChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => setSelectedOrgId(Number(value));

  const handleSubmit = () => {
    if (selectedOrgId == null) {
      return;
    }
    const candidate = candidates.find(c => c.organizationId === selectedOrgId);
    if (candidate) {
      onPickCandidate(candidate);
      return;
    }
    const suggestion = signupSuggestions.find(s => s.organizationId === selectedOrgId);
    const ssoUrl = suggestion ? ssoEntryUrl(suggestion) : null;
    if (ssoUrl) {
      hardRedirectTo(ssoUrl);
    }
  };

  // Radio groups don't trigger implicit form submission, so wire Enter explicitly once an org is selected.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && selectedOrgId != null) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const legend = hasCandidates
    ? 'You have access to multiple organizations. Choose one to continue:'
    : 'Sign in with SSO to join an organization that manages your email domain:';

  return (
    <form
      className={sharedStyles.card}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <h1 className={sharedStyles.heading}>Choose an organization</h1>
      <fieldset ref={fieldsetRef} className={styles.picker}>
        <legend className={styles.legend}>{legend}</legend>
        <RadioGroup
          name="org-picker"
          value={selectedOrgId == null ? '' : String(selectedOrgId)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        >
          {hasCandidates
            ? candidates.map(candidate => (
              <LabeledRadio
                key={candidate.organizationId}
                value={String(candidate.organizationId)}
                label={<span className={styles.option}>{candidate.organizationName}</span>}
                />
            ))
            : signupSuggestions.map(suggestion => {
              const isLdap = suggestion.ssoMethod === 'ldap';
              return (
                <LabeledRadio
                  key={suggestion.organizationId}
                  value={String(suggestion.organizationId)}
                  isDisabled={isLdap}
                  label={
                    <span className={styles.option}>
                      <span>{suggestion.organizationName}</span>
                      <small className={styles.optionSecondary}>
                        {isLdap
                          ? 'LDAP — use the standard login form'
                          : `Sign in with ${SSO_METHOD_LABEL[suggestion.ssoMethod]}`}
                      </small>
                    </span>
                    }
                  />
              );
            })}
        </RadioGroup>
      </fieldset>
      <Button isFullWidth size="large" isDisabled={selectedOrgId == null} onClick={handleSubmit}>
        Next
      </Button>
      <UseDifferentAccountButton onClick={onBack} />
    </form>
  );
};

export default OrgPickerStep;
